/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject, OnDestroy } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	Subject,
	Subscription,
	distinctUntilChanged,
	map,
	of,
	switchMap,
} from 'rxjs';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';

import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { IFirebaseList } from '@touchplan/common';

import firebase from 'firebase/compat/app';

export interface ISwimlaneDropdownOption {
	$id: string;
	planId: string;
	compositePlanSwimlaneId: string;
	color: string;
	label: string;
}

/**
 * This interface represents a group of swimlane dropdown options, collapsed by their labels.
 * It will retain the properties of the first matched swimlane, but it also adds
 * the 'compositePlanSwimlaneIdAggregate' property to keep track of the swimlanes that were collapsed
 * inside this dropdown option.
 */
export interface ICollapsedSwimlaneDropdownOption extends ISwimlaneDropdownOption {
	compositePlanSwimlaneIdAggregate: string[];
	/** duplicates the map key for use by mat-extended-select */
	key: string;
}

interface IFirebaseSwimlane {
	bottom: number;
	color: string;
	label: string;
	top: number;
}

export interface IFirebaseProjectPlanSwimlanes {
	[key: string]: IFirebaseList<IFirebaseSwimlane>;
}
/**
 * This service is designed to get all project swimlanes for report builder dropdown.
 */
@Injectable({
	providedIn: 'root',
})
export class ReportsSwimlaneDataService implements OnDestroy {
	private angularFireDatabase: AngularFireDatabase = inject(AngularFireDatabase);
	private swimlaneDropdownDataSubscription: Subscription;
	private projectId$: Subject<string> = new Subject<string>();

	private _swimlanesDropdownData$: BehaviorSubject<Map<string, ISwimlaneDropdownOption>> =
		new BehaviorSubject(new Map());

	/**
	 * This observable emit the siwmlanes dropdown state
	 */
	public swimlanesDropdownData$: Observable<Map<string, ISwimlaneDropdownOption>> =
		this._swimlanesDropdownData$.asObservable();

	/**
	 * This method listen for project id changes to update dropdown swimlanes state.
	 * @param projectId the projectId used to load swimlanes data.
	 */
	public loadSwimlaneDropdownData(projectId: string | null): void {
		// Checking for an existing subscription helps prevent multiple subscriptions to the same observable.
		if (!this.swimlaneDropdownDataSubscription) {
			this.swimlaneDropdownDataSubscription = this.listenSwimlanesDropdownData$().subscribe(
				this._swimlanesDropdownData$
			);
		}
		this.projectId$.next(projectId);
	}

	/**
	 * Since this service is root scoped, the ngOnDestroy will never be called because its lifecycle is tied
	 * to the application lifecycle. However, at some point, we might want to change this service to be
	 * component-scoped in the future as soon as we refactor some of the ticketspace.html from AngularJS to Angular
	 */
	public ngOnDestroy(): void {
		if (this.swimlaneDropdownDataSubscription) {
			this.swimlaneDropdownDataSubscription.unsubscribe();
		}
		this.projectId$.complete();
		this._swimlanesDropdownData$.complete();
	}

	private listenSwimlanesDropdownData$(): Observable<Map<string, ISwimlaneDropdownOption>> {
		return this.projectId$.asObservable().pipe(
			distinctUntilChanged(),
			switchMap((projectId) => {
				if (!projectId) {
					return of(new Map());
				}
				return this.getSwimlanesAngularFireObject(projectId)
					.valueChanges()
					.pipe(map((firebaseData) => this.transformFirebaseDataToSwimlaneDropdownOption(firebaseData)));
			})
		);
	}

	private getSwimlanesAngularFireObject(projectId: string): AngularFireObject<IFirebaseProjectPlanSwimlanes> {
		const path = fancyFirebaseGetters.getSwimlanesPath(projectId);
		return this.angularFireDatabase.object<IFirebaseProjectPlanSwimlanes>(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId)
		);
	}

	private mapFirebaseSwimlaneToSwimlaneDropdown(
		planId: string,
		firebaseSwimlaneId: string,
		firebaseSwimlane: IFirebaseSwimlane
	): ISwimlaneDropdownOption {
		return {
			$id: firebaseSwimlaneId,
			planId: planId,
			compositePlanSwimlaneId: `${planId},${firebaseSwimlaneId}`,
			color: firebaseSwimlane.color,
			label: firebaseSwimlane.label || '',
		};
	}

	private flatFirebaseProjectPlanSwimlanes(
		firebaseProjectPlanSwimlanes: IFirebaseProjectPlanSwimlanes
	): ISwimlaneDropdownOption[] {
		return Object.keys(firebaseProjectPlanSwimlanes)
			.map((planId) => {
				return Object.keys(firebaseProjectPlanSwimlanes[planId]).map((swimlaneId) =>
					this.mapFirebaseSwimlaneToSwimlaneDropdown(
						planId,
						swimlaneId,
						firebaseProjectPlanSwimlanes[planId][swimlaneId]
					)
				);
			})
			.flat();
	}

	private transformFirebaseDataToSwimlaneDropdownOption(
		firebaseProjectPlanSwimlanes: IFirebaseProjectPlanSwimlanes
	): Map<string, ISwimlaneDropdownOption> {
		if (!firebaseProjectPlanSwimlanes) {
			return new Map([]);
		}
		return new Map(
			this.flatFirebaseProjectPlanSwimlanes(firebaseProjectPlanSwimlanes).map((swimlaneDropdownOption) => [
				swimlaneDropdownOption.compositePlanSwimlaneId,
				swimlaneDropdownOption,
			])
		);
	}

	/**
	 * This function collapses a swimlane dropdown option list based on its label.
	 * @param swimlaneDropdownList - The label used to collapse the list of swimlane dropdown options.
	 * @returns - A list of collapsed dropdown options grouped by label.
	 */
	static collapseDropdownOptionsByLabel(
		swimlaneDropdownList: Map<string, ISwimlaneDropdownOption>
	): Map<string, ICollapsedSwimlaneDropdownOption> {
		const collapsedFilteredList: Map<string, ICollapsedSwimlaneDropdownOption> = new Map();

		for (let [compositePlanSwimlaneId, swimlaneDropdown] of swimlaneDropdownList) {
			const swimlaneDropdownKey = swimlaneDropdown.label
				? swimlaneDropdown.label.toLocaleLowerCase().trim()
				: null;
			// Swimlanes with not labels must be filtered out
			if (swimlaneDropdownKey && swimlaneDropdownKey !== '') {
				if (!collapsedFilteredList.get(swimlaneDropdownKey)) {
					const collapsedSwimlaneDropdown = {
						...swimlaneDropdown,
						key: swimlaneDropdownKey,
						compositePlanSwimlaneIdAggregate: [compositePlanSwimlaneId],
					};
					collapsedFilteredList.set(swimlaneDropdownKey, collapsedSwimlaneDropdown);
				} else {
					const currenCollapsedSwimlaneDropdown = collapsedFilteredList.get(swimlaneDropdownKey);
					currenCollapsedSwimlaneDropdown.compositePlanSwimlaneIdAggregate.push(compositePlanSwimlaneId);
					collapsedFilteredList.set(swimlaneDropdownKey, currenCollapsedSwimlaneDropdown);
				}
			}
		}

		return collapsedFilteredList;
	}
}
